import { useEffect } from "react";
import Newsletter from "../components/Newsletter";
import useCanonical from "../lib/useCanonical";

const LAST_UPDATED = "May 2026";

export default function Terms() {
  useCanonical("/terms");
  useEffect(() => {
    document.title = "Terms of Use — The Tea List";
    return () => {
      document.title = "The Tea List — Australia's independent tea journal";
    };
  }, []);

  return (
    <div className="relative z-10" data-testid="terms-page">
      <article className="max-w-3xl mx-auto px-6 md:px-12 pt-10 md:pt-14 pb-16">
        <p className="overline mb-3">The Tea List</p>
        <h1 className="headline-xl text-5xl md:text-6xl">Terms of Use</h1>
        <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-[#66726d]">
          Last updated · {LAST_UPDATED}
        </p>

        <div className="mt-12 prose-article max-w-2xl text-lg leading-relaxed space-y-6">
          <p>
            These Terms govern your use of <strong>The Tea List</strong>
            {" "}(thetealist.com) and any related APIs, feeds, or services
            (together, "the Site"). By browsing the Site, subscribing to our
            newsletter, leaving a comment, or otherwise interacting with us,
            you agree to these Terms. If you don't agree, please don't use
            the Site.
          </p>

          <h2 className="font-serif text-2xl mt-12 mb-2">1. Editorial nature</h2>
          <p>
            The Tea List is a personal editorial publication. Articles
            represent the opinions, reflections, and good-faith observations
            of their authors. While we fact-check our work, we do not
            guarantee that every claim, recommendation, or historical detail
            is free from error. Tea is, after all, a 1,200-year-old subject
            with a lot of competing histories.
          </p>

          <h2 className="font-serif text-2xl mt-12 mb-2">2. No professional advice</h2>
          <p>
            Nothing on the Site constitutes medical, nutritional, legal,
            financial, or professional advice. Articles about tea and health
            are written for general interest only. If you have a medical
            condition, are pregnant, are taking medication, or are
            considering significant dietary changes, please consult a
            qualified healthcare professional. Tea is generally lovely, but
            we are not your doctor.
          </p>

          <h2 className="font-serif text-2xl mt-12 mb-2">3. Affiliate links and sponsored content</h2>
          <p>
            From time to time, articles on the Site may contain affiliate
            links or sponsored content. When that's the case, we will say
            so plainly in the article. If you click an affiliate link and
            buy something, we may earn a small commission at no extra cost
            to you. Our editorial views remain our own; nobody pays us to
            change them.
          </p>

          <h2 className="font-serif text-2xl mt-12 mb-2">4. Comments and other user submissions</h2>
          <p>
            If you submit a comment, you confirm that:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>The content is your own and does not infringe any rights.</li>
            <li>It is not unlawful, defamatory, hateful, harassing, or spam.</li>
            <li>You grant us a non-exclusive, royalty-free, worldwide licence to host and display it on the Site.</li>
          </ul>
          <p>
            We may, at our discretion and without notice, edit, hide,
            delete, or refuse to publish any comment. We are not obliged
            to monitor every submission and we don't endorse the views of
            commenters.
          </p>

          <h2 className="font-serif text-2xl mt-12 mb-2">5. Intellectual property</h2>
          <p>
            All original text, photographs, illustrations and design on the
            Site are owned by The Tea List or used with permission. You may
            quote brief excerpts (a paragraph or two) for personal,
            non-commercial purposes with a clear link back to the source. For
            anything more — translations, reprints, commercial use,
            republishing in any anthology — please email us first.
          </p>
          <p>
            "The Tea List" name and logo are unregistered trademarks of the
            site's operator. Please don't use them in a way that suggests
            affiliation, endorsement or sponsorship without our written
            consent.
          </p>

          <h2 className="font-serif text-2xl mt-12 mb-2">6. External links</h2>
          <p>
            We link out to other sites for context and further reading. We
            don't control those sites and aren't responsible for their
            content, policies, or practices. Following a link takes you
            outside our care.
          </p>

          <h2 className="font-serif text-2xl mt-12 mb-2">7. Acceptable use</h2>
          <p>You agree not to:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Use the Site or its APIs in a way that interferes with normal operation.</li>
            <li>Scrape the Site at a rate that places undue load on the server.</li>
            <li>Attempt to gain unauthorised access to any part of the Site.</li>
            <li>Impersonate The Tea List or any of its authors.</li>
            <li>Republish substantial portions of our content without permission.</li>
          </ul>

          <h2 className="font-serif text-2xl mt-12 mb-2">8. Availability and changes</h2>
          <p>
            We work to keep the Site available, but it is provided
            "as is" and "as available". We may change, suspend, or
            discontinue any part of it (including these Terms) at any time
            without prior notice. Material changes to these Terms will be
            announced through the newsletter and the "Last updated" date
            above.
          </p>

          <h2 className="font-serif text-2xl mt-12 mb-2">9. Limitation of liability</h2>
          <p>
            To the fullest extent permitted by law, The Tea List, its
            authors, contributors, and affiliates will not be liable for
            any indirect, incidental, special, consequential, or punitive
            damages arising out of or relating to your use of the Site, or
            for any loss of profits, revenues, data, goodwill, or other
            intangible losses, even if we have been advised of the
            possibility of such damages. Where liability cannot be excluded
            under applicable law, it is limited to the maximum extent
            permitted by that law. Nothing in these Terms limits your
            non-excludable rights under the Australian Consumer Law or
            equivalent consumer protection law in your jurisdiction.
          </p>

          <h2 className="font-serif text-2xl mt-12 mb-2">10. Indemnity</h2>
          <p>
            You agree to indemnify and hold The Tea List harmless against
            any claim, loss, or expense (including reasonable legal fees)
            arising from your breach of these Terms or your misuse of the
            Site.
          </p>

          <h2 className="font-serif text-2xl mt-12 mb-2">11. Governing law</h2>
          <p>
            These Terms are governed by the laws of the State of Victoria,
            Australia. Any dispute arising out of or in connection with
            these Terms is subject to the exclusive jurisdiction of the
            courts of Victoria, Australia, save where applicable consumer
            law confers an exclusive jurisdiction in your home country.
          </p>

          <h2 className="font-serif text-2xl mt-12 mb-2">12. Severability</h2>
          <p>
            If any part of these Terms is found unenforceable, the rest
            will remain in full effect.
          </p>

          <h2 className="font-serif text-2xl mt-12 mb-2">13. Contact</h2>
          <p>
            Questions about these Terms — or anything else — go to{" "}
            <a
              href="mailto:sameera@thetealist.com"
              className="text-[#1e4a35] underline decoration-dotted underline-offset-4"
            >
              sameera@thetealist.com
            </a>
            .
          </p>
        </div>
      </article>

      <Newsletter />
    </div>
  );
}
